<template>
  <b-card class="fundreports-card" no-body>
    <h1>{{ title }}</h1>

    <div
      class="year-actions-row d-flex justify-content-between align-items-center flex-wrap my-3"
    >
      <div class="year-tabs d-flex flex-wrap gap-2">
        <b-button
          v-for="year in availableYears"
          :key="year"
          :variant="selectedYear === year ? 'primary' : 'outline-primary'"
          @click="selectedYear = year"
        >
          {{ year }}
        </b-button>
      </div>

      <div class="buttons-wrapper d-flex flex-column gap-2">
        <b-button
          variant="outline-primary"
          @click="updateDocuments"
          :disabled="isLoading"
        >
          <i class="fas fa-sync me-1"></i>
          Atualizar
        </b-button>

        <b-progress
          v-if="isLoading"
          :value="progress"
          max="100"
          height="15px"
          animated
          variant="primary"
        ></b-progress>
      </div>
    </div>

    <b-card-body>
      <div class="fundreports-category-buttons">
        <button
          v-for="(category, index) in categories"
          :key="index"
          :class="[
            'fundreports-toggle-btn',
            { active: activeCategory === index },
          ]"
          @click="toggleCategory(index)"
        >
          {{ category.name }}
        </button>
      </div>

      <div
        v-if="activeCategory !== null"
        class="fundreports-category-content-scroll"
      >
        <ul class="fundreports-report-items list-unstyled">
          <li
            v-for="(item, i) in filteredCategories[activeCategory]"
            :key="i"
            class="fundreports-report-item d-flex justify-content-between align-items-center p-2 mb-1 bg-light rounded"
          >
            <div class="fundreports-item-left">
              <span class="fundreports-item-title">{{ item.title }}</span>
              <span class="fundreports-item-date text-muted ms-2">{{
                item.date
              }}</span>
            </div>

            <div class="d-flex align-items-center gap-2">
              <b-button
                variant="link"
                size="sm"
                class="p-0"
                title="Download"
                @click.stop="downloadFile(item.url)"
              >
                <i class="fas fa-download"></i>
              </b-button>

              <b-button
                variant="link"
                size="sm"
                class="p-0"
                title="Visualizar"
                @click.stop="openViewer(item.url, item.title)"
              >
                <i class="fas fa-eye"></i>
              </b-button>
            </div>
          </li>
        </ul>
      </div>
    </b-card-body>

    <b-modal
      id="reports-viewer-modal"
      size="xl"
      :title="`Visualizar Report - ${currentDocument || ''}`"
      hide-footer
    >
      <div v-if="!iframeBlocked">
        <iframe
          v-if="currentUrl"
          :src="currentUrl"
          width="100%"
          height="800px"
          style="border: none"
        ></iframe>
      </div>

      <div v-else class="text-center py-5">
        <p class="mb-3">
          Este site não permite ser visualizado dentro da plataforma. <br />
          Para acessá-lo, clique no botão abaixo:
        </p>
        <b-button
          :href="currentUrl"
          target="_blank"
          variant="primary"
          rel="noopener noreferrer"
        >
          Abrir em nova aba
        </b-button>
      </div>
    </b-modal>

    <AddDocumentModal
      ref="addDocumentModal"
      @save-document="handleSaveDocument"
    />
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BButton,
  BModal,
  BSpinner,
  BProgress,
} from "bootstrap-vue";
import api from "@/views/pages/FundsHub/Requests.js";
import AddDocumentModal from "./AddDocumentModal.vue";

export default {
  name: "FundReports",
  components: {
    BCard,
    BCardBody,
    BButton,
    BModal,
    BSpinner,
    BProgress,
    AddDocumentModal,
  },
  data() {
    return {
      title: "Banco Central do Brasil",
      activeCategory: null,
      currentUrl: null,
      currentDocument: null,
      categories: [],
      selectedYear: null,
      availableYears: [],
      iframeBlocked: false,
      isLoading: false,
      progress: 0,
    };
  },
  computed: {
    filteredCategories() {
      if (!this.selectedYear) return this.categories.map((c) => c.items);
      return this.categories.map((c) =>
        c.items.filter((item) => item.date.includes(this.selectedYear))
      );
    },
  },
  created() {
    this.fetchCategories();
  },
  methods: {
    async fetchCategories() {
      try {
        const response = await api.getGroupedReports();

        const categories = response.data.map((category) => ({
          name: category.name,
          items: category.documents.flatMap((doc) => doc.items || []),
        }));

        const allYears = new Set();
        categories.forEach((category) => {
          category.items.forEach((item) => {
            const year = item.date?.split("/")?.[2];
            if (year) allYears.add(year);
          });
        });

        this.availableYears = Array.from(allYears).sort((a, b) => b - a);
        this.selectedYear = this.availableYears[0] || null;
        this.categories = categories;
      } catch (error) {
        console.error("Erro ao carregar categorias de reports:", error);
      }
    },

    toggleCategory(index) {
      this.activeCategory = this.activeCategory === index ? null : index;
    },

    openViewer(url, title) {
      this.currentDocument = title;
      if (url.toLowerCase().endsWith(".pdf")) {
        this.currentUrl = url;
        this.iframeBlocked = false;
        this.$root.$emit("bv::show::modal", "reports-viewer-modal");
      } else {
        window.open(url, "_blank", "noopener");
      }
    },

    downloadFile(url) {
      window.open(url, "_blank", "noopener");
    },

    async updateDocuments() {
      this.isLoading = true;
      this.progress = 0;

      const interval = setInterval(() => {
        if (this.progress < 90) this.progress += 5;
      }, 200);

      try {
        await api.updateReports();
        await new Promise((resolve) => setTimeout(resolve, 500));
        this.progress = 100;

        this.$bvToast.toast("Documentos atualizados com sucesso", {
          title: "Sucesso",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });

        await this.fetchCategories();
      } catch (error) {
        this.$bvToast.toast("Erro ao atualizar os documentos.", {
          title: "Erro",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000,
        });
      } finally {
        clearInterval(interval);
        setTimeout(() => {
          this.isLoading = false;
          this.progress = 0;
        }, 500);
      }
    },
    addDocument() {
      if (this.$refs.addDocumentModal?.show) {
        this.$refs.addDocumentModal.show();
      }
    },

    handleSaveDocument(newDoc) {
      api
        .addReport({
          name: newDoc.manager,
          website_url: newDoc.website_url,
          category: newDoc.fund_type,
        })
        .then(() => {
          this.$bvToast.toast("Instituição adicionada com sucesso", {
            title: "Sucesso",
            variant: "success",
            solid: true,
            autoHideDelay: 3000,
          });
          this.fetchCategories();
        })
        .catch(() => {
          this.$bvToast.toast("Erro ao adicionar instituição.", {
            title: "Erro",
            variant: "danger",
            solid: true,
            autoHideDelay: 4000,
          });
        });
    },
  },
};
</script>

<style lang="scss">
@import "src/@core/scss/base/components/fund-reports.scss";
</style>
